/**
 * B709 (ii) — the membership checkout (Figma 636:2 @1440): WHO'S COVERED on
 * the left (named adults as on their ID + the email the cards go to, the
 * household's children, the add-ons, the renewal), YOUR MEMBERSHIP on the
 * right (the term, the lines, Total today, the fair-market-value line when
 * the venue prints it, CONTINUE TO PAYMENT, BACK TO PLANS). Then the
 * provider's card page (PAY) and DONE. The shapes and every sentence come
 * from lib/membershipCheckout.ts; this file lays them out. Built from the
 * PNG (redline pending).
 *
 * B709 (a3) — 636:2's gift card: "This is a gift for someone else". On, the
 * form asks who it's for (their email, their name), who it's from and a note,
 * the buyer's email takes the receipt, and what the recipient decides at
 * redemption (the named adults, the children, renewal) is not asked.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { Banner, Button, Checkbox, Input, PaymentWait, Skeleton, Stepper, Switch } from "@transact/ui";
import type { SetupGateway } from "../lib/gateway.js";
import { formatMoney } from "../lib/money.js";
import type { OfferPlan, OfferTier } from "../lib/membershipsPage.js";
import {
  EMPTY_GIFT,
  adultsNote,
  asksZip,
  childrenNote,
  giftNote,
  termLine,
  type BuyerSwitches,
  type CheckoutDraft,
  type CheckoutField,
  type CheckoutQuote,
} from "../lib/membershipCheckout.js";

export type CheckoutStep = "PLAN" | "WHO" | "PAY" | "DONE";
const STEPS: readonly { readonly step: CheckoutStep; readonly label: string }[] = [
  { step: "PLAN", label: "Plan" },
  { step: "WHO", label: "Who's covered" },
  { step: "PAY", label: "Pay" },
  { step: "DONE", label: "Done" },
];

export function CheckoutSteps(props: { readonly at: CheckoutStep }): ReactElement {
  const at = STEPS.findIndex((s) => s.step === props.at);
  return (
    <ol className="mbuy-steps" aria-label="Checkout steps" data-redline="mbuy-steps">
      {STEPS.map((s, i) => (
        <li key={s.step} className={i < at ? "mbuy-step mbuy-step--done" : i === at ? "mbuy-step mbuy-step--now" : "mbuy-step"} aria-current={i === at ? "step" : undefined}>
          <span className="mbuy-dot" aria-hidden="true" />
          {s.label}
          {i < at ? <span className="t-visually-hidden"> — done</span> : null}
        </li>
      ))}
    </ol>
  );
}

export function MembershipCheckout(props: {
  readonly plan: OfferPlan;
  readonly tier: OfferTier;
  readonly draft: CheckoutDraft;
  readonly switches: BuyerSwitches;
  readonly now: number;
  /** The venue's zone; absent = the term is said without dates (B659). */
  readonly timeZone?: string | undefined;
  readonly quote: CheckoutQuote;
  readonly consent: string;
  /** Each field's refusal, shown once the buyer has pressed CONTINUE (R18: at the field). */
  readonly issues: Partial<Record<CheckoutField, string>>;
  /** The door's own answer in words (a refused card, a stale total, a card never saved). */
  readonly banner?: { readonly tone: "danger" | "info"; readonly text: string } | undefined;
  readonly busy: boolean;
  readonly onDraft: (next: CheckoutDraft) => void;
  readonly onContinue: () => void;
  readonly onBack: () => void;
}): ReactElement {
  const { plan, tier, draft, switches, quote } = props;
  const set = (patch: Partial<CheckoutDraft>): void => props.onDraft({ ...draft, ...patch });
  const gift = draft.gift;
  const setGift = (patch: Partial<NonNullable<CheckoutDraft["gift"]>>): void => set({ gift: { ...(gift ?? EMPTY_GIFT), ...patch } });
  const toggleGift = (on: boolean): void => {
    if (on) {
      set({ gift: gift ?? EMPTY_GIFT, autoRenew: false });
      return;
    }
    const { gift: _off, ...self } = draft;
    props.onDraft(self);
  };
  // The recipient decides who is covered at redemption: a gift asks no children.
  const maxChildren = gift !== undefined ? 0 : tier.covers.householdChildren.max;
  const span = plan.kind === "ANNUAL" ? "12 months" : "1 month";
  return (
    <div className="mbuy" data-redline="mbuy">
      <CheckoutSteps at="WHO" />
      <h1 className="mbuy-title" data-redline="mbuy-title">
        {gift !== undefined ? "Who's this gift for?" : "Who's on this membership?"}
      </h1>
      {props.banner !== undefined ? <Banner tone={props.banner.tone}>{props.banner.text}</Banner> : null}
      {/* B683 (R14) — one form: Enter in any field presses CONTINUE TO PAYMENT, under the same checks. */}
      <form
        className="mbuy-grid"
        aria-label={gift !== undefined ? "Who's this gift for" : "Who's on this membership"}
        onSubmit={(e) => {
          e.preventDefault();
          if (!props.busy) props.onContinue();
        }}
      >
        <div className="mbuy-form">
          {gift !== undefined ? (
            <section className="mships-card mbuy-card" aria-labelledby="mbuy-gift-cap" data-redline="mbuy-gift-who">
              <h2 className="mships-section-cap" id="mbuy-gift-cap">
                The gift
              </h2>
              <p className="mships-note mbuy-lede">{`We email them a voucher. They name who's covered and start the ${span} when they redeem it.`}</p>
              <div className="mbuy-pair">
                <Input
                  id="mbuy-gift-email"
                  label="Their email"
                  type="email"
                  value={gift.recipientEmail}
                  onChange={(v) => setGift({ recipientEmail: v })}
                  placeholder="sam@example.com"
                  hint="The voucher goes here, with the link to redeem it."
                  error={props.issues["gift-email"]}
                />
                <Input id="mbuy-gift-name" label="Their name (optional)" value={gift.recipientName} onChange={(v) => setGift({ recipientName: v })} placeholder="Sam Lee" hint="So the voucher greets them by name." />
              </div>
              <div className="mbuy-pair">
                <Input
                  id="mbuy-giver"
                  label="Your name"
                  value={gift.giverName}
                  onChange={(v) => setGift({ giverName: v })}
                  placeholder="Priya Natarajan"
                  hint="The voucher says it's from you."
                  error={props.issues["gift-giver"]}
                />
                <Input
                  id="mbuy-email"
                  label="Your email"
                  type="email"
                  value={draft.email}
                  onChange={(v) => set({ email: v })}
                  placeholder="priya@example.com"
                  hint="Your receipt goes here, and we tell you when they redeem it."
                  error={props.issues.email}
                />
              </div>
              <Input id="mbuy-gift-message" label="A note (optional)" value={gift.message} onChange={(v) => setGift({ message: v })} placeholder="Happy birthday — see you at the sea lions!" hint="Printed on the voucher as you write it." />
            </section>
          ) : (
          <section className="mships-card mbuy-card" aria-labelledby="mbuy-adults-cap" data-redline="mbuy-adults">
            <h2 className="mships-section-cap" id="mbuy-adults-cap">
              {`Named adults · ${String(draft.adults.length)} of ${String(tier.covers.namedAdults)}`}
            </h2>
            <p className="mships-note mbuy-lede" data-redline="mbuy-adults-note">{adultsNote(tier, switches)}</p>
            {draft.adults.map((name, i) => (
              <div key={i} className="mbuy-pair">
                <Input
                  id={`mbuy-adult-${String(i)}`}
                  label="Full name as on ID" // id-ok: a photo ID card the gate checks, not a lookup id
                  value={name}
                  onChange={(v) => set({ adults: draft.adults.map((a, j) => (j === i ? v : a)) })}
                  placeholder={i === 0 ? "Priya Natarajan" : "Arjun Natarajan"}
                  error={props.issues[`adult-${String(i)}`]}
                />
                {i === 0 ? (
                  <Input
                    id="mbuy-email"
                    label="Email"
                    type="email"
                    value={draft.email}
                    onChange={(v) => set({ email: v })}
                    placeholder="priya@example.com"
                    hint="The cards and the receipt go here, and it's how you sign in to your membership page."
                    error={props.issues.email}
                  />
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>
            ))}
            {asksZip(tier, switches) ? (
              <Input
                id="mbuy-zip"
                label="Home ZIP code"
                value={draft.zip}
                onChange={(v) => set({ zip: v })}
                placeholder="85001"
                width="sm"
                hint={`The ${tier.name} price is for residents — the gate may ask for proof of address.`}
                error={props.issues.zip}
              />
            ) : null}
          </section>
          )}

          <section className="mships-card mbuy-card" aria-labelledby="mbuy-kids-cap" data-redline="mbuy-household">
            {maxChildren > 0 ? (
              <div className="mbuy-kids">
                <div>
                  <h2 className="mships-section-cap" id="mbuy-kids-cap">
                    Children in your household
                  </h2>
                  <p className="mships-note mbuy-lede" data-redline="mbuy-kids-note">{childrenNote(tier)}</p>
                </div>
                <Stepper label="Children in your household" value={draft.children} min={0} max={maxChildren} onChange={(n) => set({ children: n })} decreaseLabel="One fewer child" increaseLabel="One more child" />
              </div>
            ) : (
              <h2 className="t-visually-hidden" id="mbuy-kids-cap">
                Add-ons
              </h2>
            )}
            {tier.addOns.length > 0 ? (
              <div className="mbuy-addons">
                <h3 className="mships-section-cap">Add-ons</h3>
                {tier.addOns.map((a) => (
                  <div key={a.addOnId} className="mbuy-addon">
                    <Checkbox
                      id={`mbuy-addon-${a.addOnId}`}
                      label={a.name}
                      checked={draft.addOns.includes(a.addOnId)}
                      onChange={(on) => set({ addOns: on ? [...draft.addOns, a.addOnId] : draft.addOns.filter((x) => x !== a.addOnId) })}
                    />
                    <span className="mbuy-addon-price">{`+${formatMoney(a.price).replace(/\.00$/, "")}`}</span>
                  </div>
                ))}
              </div>
            ) : maxChildren > 0 ? null : (
              <p className="mships-note">{`${tier.name} has no add-ons — everything it covers is on the card.`}</p>
            )}
          </section>

          {/* 636:2 — the gift card, between the add-ons and renewal */}
          <section className="mships-card mbuy-card" aria-label="Gift" data-redline="mbuy-gift">
            <Switch id="mbuy-gift" label="This is a gift for someone else" checked={gift !== undefined} onChange={toggleGift} hint={giftNote(plan)} />
          </section>

          {gift !== undefined ? null : (
            <section className="mships-card mbuy-card mbuy-renew" aria-labelledby="mbuy-renew-cap" data-redline="mbuy-renewal">
              <h2 className="mships-section-cap" id="mbuy-renew-cap">
                Renewal
              </h2>
              <Checkbox
                id="mbuy-autorenew"
                label={`Renew automatically each ${plan.kind === "ANNUAL" ? "year" : "month"}`}
                checked={draft.autoRenew}
                onChange={(on) => set({ autoRenew: on })}
                hint={draft.autoRenew ? props.consent : "Leave this off to renew yourself — we'll email you before it ends."}
              />
            </section>
          )}
        </div>

        <aside className="mships-card mbuy-summary" aria-labelledby="mbuy-summary-cap" data-redline="mbuy-summary">
          <h2 className="mships-section-cap" id="mbuy-summary-cap">
            Your membership
          </h2>
          <p className="mbuy-tier">{tier.name}</p>
          <p className="mships-note mbuy-lede" data-redline="mbuy-term">{gift !== undefined ? `A gift · ${span} from the day they redeem it` : termLine(plan.kind, props.now, props.timeZone)}</p>
          <ul className="mbuy-lines">
            {quote.rows.map((r) => (
              <li key={r.label}>
                <span>{r.label}</span>
                <span>{r.value}</span>
              </li>
            ))}
          </ul>
          <p className="mbuy-total" data-redline="mbuy-total">
            <span className="mbuy-total-label" data-redline="mbuy-total-label">Total today</span>
            <span className="mbuy-total-figure">{quote.totalText}</span>
          </p>
          {quote.fmv !== undefined ? <p className="mships-note">{quote.fmv}</p> : null}
          <Button variant="primary" type="submit" busy={props.busy} onClick={() => undefined}>
            Continue to payment
          </Button>
          {props.busy ? null : (
            <Button variant="neutral" onClick={props.onBack}>
              Back to plans
            </Button>
          )}
          <p className="mships-note">You'll add your card on the payment provider's secure page, then come straight back here.</p>
        </aside>
      </form>
    </div>
  );
}

/**
 * B816 — the PAY step ON the page (Pathik, 2026-10-13: "why does the
 * membership checkout take me to stripe hosted page?"): the provider's card
 * element mounts here against the buy's setup secret; PAY confirms it and
 * the purchase finishes exactly as the way back from a hosted page did. The
 * mount is the caller's (the Stripe factory is a seam); this face owns the
 * box, the door and the refusal.
 */
export function MembershipCardStep(props: {
  readonly tierName: string;
  readonly amount?: string | undefined;
  readonly busy: boolean;
  readonly error?: string | undefined;
  readonly mount: (selector: string) => Promise<{ readonly gateway: SetupGateway } | { readonly gateway?: undefined; readonly unavailable: string }>;
  readonly onPay: (gateway: SetupGateway) => Promise<void>;
  readonly onBack: () => void;
  /** B823 — the portal's RENEW wears the same step: no checkout stepper, the renewal's own words. */
  readonly renewal?: boolean;
}): ReactElement {
  const renewal = props.renewal === true;
  const [gateway, setGateway] = useState<SetupGateway | undefined>(undefined);
  const [unavailable, setUnavailable] = useState<string | undefined>(undefined);
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    let gone = false;
    void props.mount("#membership-card-element").then((made) => {
      if (gone) return;
      if (made.gateway === undefined) setUnavailable(made.unavailable);
      else setGateway(made.gateway);
    });
    return () => {
      gone = true;
      gateway?.unmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once; the element keeps the typed card across re-renders
  }, []);
  return (
    <div className="mbuy" data-redline="mbuy-card">
      {renewal ? null : <CheckoutSteps at="PAY" />}
      <h1 className="mbuy-title">
        {renewal
          ? props.amount !== undefined
            ? `Pay ${props.amount} to renew ${props.tierName}`
            : `Renew ${props.tierName}`
          : props.amount !== undefined
            ? `Pay ${props.amount} for ${props.tierName}`
            : `Pay for ${props.tierName}`}
      </h1>
      <p className="mships-note">
        {renewal
          ? "Your card is saved securely with the payment provider and charged now for your next year. Your member number stays the same."
          : "Your card is saved securely with the payment provider and charged now; if you chose auto-renew, it renews on this card."}
      </p>
      {props.error !== undefined ? (
        <Banner tone="danger">
          {props.error}{" "}
          {/* the way back inside the refusal (B213 rule (ii)): the card stays mounted; PAY is the retry */}
          <Button variant="textlink" onClick={props.onBack}>
            {renewal ? "Back to your membership" : "Change your details"}
          </Button>
        </Banner>
      ) : null}
      {unavailable !== undefined ? (
        <Banner tone="warn">
          {`Card payment isn't available on this page right now (${unavailable}).`}{" "}
          <Button variant="textlink" onClick={props.onBack}>
            Go back and try again
          </Button>
        </Banner>
      ) : null}
      <div className="mbuy-card-box" aria-busy={gateway === undefined && unavailable === undefined}>
        {gateway === undefined && unavailable === undefined ? <Skeleton lines={3} /> : null}
        <div id="membership-card-element" data-redline="mbuy-card-element" />
      </div>
      {props.busy ? (
        <PaymentWait redline="mbuy-card-wait" note={renewal ? "Saving your card and renewing your membership." : "Saving your card and starting your membership."} />
      ) : (
        <div className="mbuy-doors">
          <Button variant="neutral" onClick={props.onBack}>
            Back
          </Button>
          {gateway !== undefined ? (
            <Button variant="primary" size="lg" onClick={() => void props.onPay(gateway)}>
              {props.amount !== undefined ? `Pay ${props.amount}` : "Pay"}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** PAY in flight, and the way back from the card page: the act named (R20). */
export function MembershipWorking(props: { readonly at: CheckoutStep; readonly text: string }): ReactElement {
  return (
    <div className="mbuy" aria-busy="true">
      <CheckoutSteps at={props.at} />
      <p className="mbuy-title" role="status">
        {props.text}
      </p>
      <Skeleton lines={4} />
    </div>
  );
}

export function MembershipDone(props: {
  readonly tierName?: string | undefined;
  readonly businessName?: string | undefined;
  readonly memberNumber: string;
  readonly cards: readonly string[];
  readonly autoRenew: boolean;
  readonly charged?: string | undefined;
  readonly email?: string | undefined;
  readonly portalUrl?: string | undefined;
  readonly ticketsUrl?: string | undefined;
  /** B709 (a3) — a gift: the voucher and who it went to; no cards until they redeem it. */
  readonly gift?: { readonly code: string; readonly to: string } | undefined;
  /** B824 — inside the embed's frame the portal opens in a tab of its own (its sign-in is an emailed link to the hosted page). */
  readonly newTab?: boolean | undefined;
}): ReactElement {
  if (props.gift !== undefined) {
    return (
      <div className="mbuy" data-redline="mbuy-done">
        <CheckoutSteps at="DONE" />
        <h1 className="mbuy-title" tabIndex={-1}>
          Your gift is on its way
        </h1>
        <section className="mships-card mbuy-card" aria-label="Your gift">
          <p className="mbuy-tier">{`${props.tierName ?? "Membership"} · a gift`}</p>
          <p className="mships-note">{`Voucher ${props.gift.code}${props.charged !== undefined ? ` · ${props.charged} paid today` : ""}`}</p>
          <p className="mships-note">
            {`We emailed the voucher to ${props.gift.to}. They name who's covered and start the membership when they redeem it, and it renews on their card only if they choose. Your receipt went to ${props.email ?? "your email"}, and we'll tell you when they redeem it.`}
          </p>
          {props.ticketsUrl !== undefined ? (
            <div className="mbuy-done-doors">
              <a className="mbuy-door mbuy-door--quiet" href={props.ticketsUrl}>
                Back to tickets
              </a>
            </div>
          ) : null}
        </section>
      </div>
    );
  }
  return (
    <div className="mbuy" data-redline="mbuy-done">
      <CheckoutSteps at="DONE" />
      <h1 className="mbuy-title" tabIndex={-1}>
        {`You're a member${props.businessName !== undefined ? ` of ${props.businessName}` : ""}`}
      </h1>
      <section className="mships-card mbuy-card" aria-label="Your membership">
        <p className="mbuy-tier">{props.tierName ?? "Membership"}</p>
        <p className="mships-note">{`Member number ${props.memberNumber}${props.charged !== undefined ? ` · ${props.charged} paid today` : ""}`}</p>
        {props.cards.length > 0 ? (
          <ul className="mbuy-lines">
            {props.cards.map((holder) => (
              <li key={holder}>
                <span>{holder}</span>
                <span>Card ready</span>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mships-note">
          {`The cards' QR codes are in the welcome email${props.email !== undefined ? ` to ${props.email}` : ""} — show one at the gate. ${props.autoRenew ? "It renews automatically; we email you before it does." : "We'll email you before it ends so you can renew."}`}
        </p>
        <div className="mbuy-done-doors">
          {props.portalUrl !== undefined ? (
            <a className="mbuy-door" href={props.portalUrl} {...(props.newTab === true ? { target: "_blank", rel: "noopener" } : {})}>
              See your membership
            </a>
          ) : null}
          {props.ticketsUrl !== undefined ? (
            <a className="mbuy-door mbuy-door--quiet" href={props.ticketsUrl}>
              Back to tickets
            </a>
          ) : null}
        </div>
      </section>
    </div>
  );
}
